import Decimal from "decimal.js";
import {
  FP_CUSTOM_SOURCE_PREFIX,
  FP_FIELD_SOURCES,
  ItemUnitSchema,
  todayDateOnlyString,
  toDateOnlyString,
  DimensionUnitSchema,
  WeightUnitSchema,
  type Address,
  type CustomFieldValues,
  type FpFieldSource,
  type ItemUnit,
  type PublicQuote,
  type PublicQuoteItem,
  type PublicQuoteLine,
  type PublicQuoteQuantityBreak,
} from "@forgepoint/shared";

/**
 * An item line carrying no values at all: no item, and a single empty quantity
 * break so exactly one row renders per line. Every line-scope binding resolves
 * empty against it, which is what draws the `⟨Label⟩` chip while editing.
 * @param id - The line's id, unique within the placeholder quote
 * @returns A valueless item line
 */
function stubItemLine(id: string): PublicQuoteLine {
  return {
    id,
    type: "ITEM",
    description: "",
    amount: null,
    quantityBreaks: [
      {
        quantity: "",
        unitPrice: null,
        unitPriceOverride: null,
        amount: null,
        totalWeight: null,
        pricingUnit: null,
      },
    ],
    item: null,
    unit: null,
  };
}

/**
 * The line structure the placeholder canvas renders: enough rows for a table to
 * take shape, carrying no values whatsoever, so every cell draws its field chip.
 */
const PLACEHOLDER_LINES: readonly PublicQuoteLine[] = [
  { id: "placeholder-section", type: "SECTION", description: "" },
  stubItemLine("placeholder-line-1"),
  stubItemLine("placeholder-line-2"),
  { id: "placeholder-note", type: "NOTE", description: "" },
];

/**
 * The stand-in quote the template editor binds, since a template belongs to the
 * organization rather than to any one quote.
 *
 * Every field is left empty on purpose: the renderer draws an empty bound field
 * as a `⟨Label⟩` placeholder chip while editing, so the author sees exactly
 * which values a real quote will supply. `createdAt` is an invalid date because
 * the type forbids null; it formats to "" so its chip draws like the rest.
 */
export const PLACEHOLDER_QUOTE: PublicQuote = {
  id: "placeholder-quote",
  status: "DRAFT",
  watermark: null,
  quoteDisplayNumber: "",
  revision: 1,
  isLatestRevision: true,
  latestRevisionId: null,
  name: null,
  deliveryTerms: null,
  paymentTerms: null,
  termsOfService: null,
  quoteFooter: null,
  coverLetterMarkdown: null,
  enableCoverLetter: true,
  expirationDate: null,
  createdAt: new Date(NaN),
  quoteDate: null,
  requiresCustomerSignature: true,
  showTotals: true,
  showTotalWeight: true,
  subtotal: null,
  subtotalWithDiscounts: null,
  total: null,
  priceDecimals: null,
  isSigned: false,
  signature: null,
  publicQuoteTemplate: "modern",
  publicAccessRevokedAt: null,
  customFields: null,
  quoteCurrency: null,
  baseCurrency: null,
  snapshot: null,
  salesRepName: null,
  salesRepEmail: null,
  estimatorNames: [],
  lines: [...PLACEHOLDER_LINES],
  organization: null,
  location: null,
  customerCompany: null,
  people: [],
  shippingAddress: null,
  opportunity: null,
};

/**
 * The directly writable sample field keys: the quote-scope sources that map to
 * exactly one value the author can type. Derived sources (a letterhead name
 * falling back from location to organization, a contact line joining website
 * and phone) are absent, because writing them has no well-defined target; they
 * expand to these keys through {@link SAMPLE_KEYS_BY_SOURCE}.
 */
export const SAMPLE_FIELD_KEYS = [
  "org-name",
  "org-address",
  "org-website",
  "org-phone",
  "org-fax",
  "location-name",
  "location-address",
  "customer-company-name",
  "customer-company-phone",
  "customer-company-email",
  "customer-company-address",
  "quote-people-names",
  "quote-people-emails",
  "ship-to-address",
  "opportunity-name",
  "sales-rep-name",
  "sales-rep-email",
  "estimator-names",
  "quote-name",
  "quote-number",
  "effective-date",
  "expiration-date",
  "quote-footer",
  "subtotal",
  "subtotal-with-discounts",
  "total",
  "signer-name",
  "signed-date",
  "cover-letter",
  "payment-terms",
  "delivery-terms",
  "terms-of-service",
] as const satisfies readonly FpFieldSource[];

/** One primitive, directly writable sample field key. */
export type SampleFieldKey = (typeof SAMPLE_FIELD_KEYS)[number];

/**
 * Hand-edited sample data for one template. Frontend-only: it never rides on an
 * API payload, and a saved template never contains any of it.
 */
export interface TemplateSampleData {
  /**
   * Overrides keyed by primitive sample key or `custom:<name>`; an absent key
   * falls back to {@link SAMPLE_FIELD_DEFAULTS}.
   */
  fields: Record<string, string>;
  /** The editable sample line rows, seeded from the placeholder lines. */
  lines: PublicQuoteLine[];
}

/**
 * The line rows sample mode starts from. Unlike the placeholder binding, whose
 * rows are deliberately valueless so every cell draws its chip, these carry
 * obviously-fake values so the author can judge column widths, alignment, and
 * wrapping against text of a realistic length.
 */
export const SAMPLE_SEED_LINES: readonly PublicQuoteLine[] = [
  { id: "sample-section", type: "SECTION", description: "Sample section" },
  buildSeedItem({
    id: "sample-line-1",
    itemName: "SAMPLE-001",
    revision: "A",
    quantity: "10",
    unitPrice: "100.00",
    amount: "1000.00",
    totalWeight: "25",
  }),
  buildSeedItem({
    id: "sample-line-2",
    itemName: "SAMPLE-002",
    revision: "B",
    quantity: "3",
    unitPrice: "150.00",
    amount: "450.00",
    totalWeight: "9",
  }),
  { id: "sample-note", type: "NOTE", description: "Sample note line." },
];

/** The values one filled sample item row is built from. */
interface SeedItemSpec {
  id: string;
  itemName: string;
  revision: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  totalWeight: string;
}

/**
 * One filled sample item row.
 * @param spec - The row's id, item identity, quantity, prices, and weight
 * @returns The seeded item row
 */
function buildSeedItem(spec: SeedItemSpec): PublicQuoteLine {
  return {
    id: spec.id,
    type: "ITEM",
    description: `Sample line item ${spec.itemName}`,
    amount: spec.amount,
    quantityBreaks: [
      {
        quantity: spec.quantity,
        unitPrice: spec.unitPrice,
        unitPriceOverride: null,
        amount: spec.amount,
        totalWeight: spec.totalWeight,
        pricingUnit: null,
      },
    ],
    item: {
      name: spec.itemName,
      revision: spec.revision,
      description: "Sample item description",
      unit: "each",
      weight: "2.5",
      weightUnit: "kg",
      length: null,
      width: null,
      height: null,
      dimensionUnit: null,
      primaryFileAsset: null,
    },
    unit: "each",
  };
}

/** The sample data a template starts from: every default, the seeded rows. */
export const DEFAULT_SAMPLE_DATA: TemplateSampleData = {
  fields: {},
  lines: [...SAMPLE_SEED_LINES],
};

/** Days a sample quote stays valid, so the `expired` condition reads false. */
const SAMPLE_VALIDITY_DAYS = 30;

/**
 * The calendar day a whole number of days from today, as `YYYY-MM-DD`. Read
 * once at module load so the canvas never re-renders on a clock tick.
 * @param days - Days to add to today
 * @returns The resulting calendar day
 */
function dateOnlyFromToday(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateOnlyString(date);
}

/** Pre-filled sample values, so sample mode shows a plausible quote at once. */
export const SAMPLE_FIELD_DEFAULTS: Record<SampleFieldKey, string> = {
  "org-name": "Acme Manufacturing",
  "org-address": "1200 Foundry Road, Detroit, MI 48201",
  "org-website": "acme-manufacturing.com",
  "org-phone": "(555) 010-4477",
  "org-fax": "(555) 010-4478",
  "location-name": "Detroit Plant",
  "location-address": "1200 Foundry Road, Detroit, MI 48201",
  "customer-company-name": "Northwind Industrial",
  "customer-company-phone": "(555) 020-8899",
  "customer-company-email": "purchasing@northwind-industrial.com",
  "customer-company-address": "88 Harbor Way, Seattle, WA 98104",
  "quote-people-names": "Morgan Lee",
  "quote-people-emails": "morgan.lee@northwind-industrial.com",
  "ship-to-address": "Receiving Dock 4, 88 Harbor Way, Seattle, WA 98104",
  "opportunity-name": "Pump Housing Program",
  "sales-rep-name": "Dana Reyes",
  "sales-rep-email": "dana.reyes@acme-manufacturing.com",
  "estimator-names": "Casey Park",
  "quote-name": "Pump Housing Assembly",
  "quote-number": "Q-2026-0042",
  "effective-date": todayDateOnlyString(),
  "expiration-date": dateOnlyFromToday(SAMPLE_VALIDITY_DAYS),
  "quote-footer": "Thank you for the opportunity to quote.",
  subtotal: "1450.00",
  "subtotal-with-discounts": "1450.00",
  total: "1450.00",
  "signer-name": "Jordan Blake",
  "signed-date": todayDateOnlyString(),
  "cover-letter":
    "Thank you for your interest in Acme Manufacturing. We are pleased to quote the parts below and look forward to working with you.",
  "payment-terms": "Net 30 from date of invoice.",
  "delivery-terms": "FOB origin, 4-6 weeks after receipt of order.",
  "terms-of-service":
    "Prices are valid for 30 days. Tooling charges are non-refundable.",
};

/**
 * The values the organization has actually configured, keyed by sample key. An
 * absent, null, or blank entry means "not configured" and leaves the invented
 * default in place. Resolved by the caller from the settings and organization
 * stores, so this module stays pure.
 */
export type ConfiguredSampleValues = Partial<
  Record<SampleFieldKey, string | null>
>;

/**
 * The value a sample field starts from: what the organization configured when
 * it configured anything, else the invented default.
 * @param key - The primitive sample key
 * @param configured - The organization's configured values, keyed by sample key
 * @returns The default value for that key, never empty
 */
export function resolveSampleDefault(
  key: SampleFieldKey,
  configured: ConfiguredSampleValues = {},
): string {
  const value = configured[key] ?? "";
  return value.trim() === "" ? SAMPLE_FIELD_DEFAULTS[key] : value;
}

/**
 * The value a sample field currently holds: the author's hand edit when there
 * is one, else the resolved default. The form and {@link buildSampleQuote} both
 * read through this, so the inputs and the canvas cannot drift apart.
 * @param key - The primitive sample key
 * @param fields - The template's sample field overrides
 * @param configured - The organization's configured values, keyed by sample key
 * @returns The value to show in the input and bind on the canvas
 */
export function resolveSampleValue(
  key: SampleFieldKey,
  fields: Record<string, string>,
  configured: ConfiguredSampleValues = {},
): string {
  return fields[key] ?? resolveSampleDefault(key, configured);
}

/**
 * Every quote-scope source expanded to the primitive keys that feed it, so the
 * sample form can offer one input per writable value. Total over the source
 * vocabulary, so a new source is a compile error until it is mapped.
 */
export const SAMPLE_KEYS_BY_SOURCE: Record<
  FpFieldSource,
  readonly SampleFieldKey[]
> = {
  "org-name": ["org-name"],
  "org-address": ["org-address"],
  "org-website": ["org-website"],
  "org-phone": ["org-phone"],
  "org-fax": ["org-fax"],
  "org-headquarters": ["org-name", "org-address"],
  "org-contact-line": ["org-website", "org-phone"],
  location: ["location-name"],
  "location-name": ["location-name"],
  "location-address": ["location-address"],
  "letterhead-name": ["location-name", "org-name"],
  "letterhead-address": ["location-address", "org-address"],
  "sales-rep-name": ["sales-rep-name"],
  "sales-rep-email": ["sales-rep-email"],
  "estimator-names": ["estimator-names"],
  "customer-name": ["customer-company-name"],
  "customer-company-name": ["customer-company-name"],
  "customer-company-phone": ["customer-company-phone"],
  "customer-company-email": ["customer-company-email"],
  "customer-company-address": ["customer-company-address"],
  "quote-people-names": ["quote-people-names"],
  "quote-people-emails": ["quote-people-emails"],
  "ship-to-address": ["ship-to-address"],
  "opportunity-name": ["opportunity-name"],
  "quote-name": ["quote-name"],
  "quote-number": ["quote-number"],
  "effective-date": ["effective-date"],
  "expiration-date": ["expiration-date"],
  "quote-footer": ["quote-footer"],
  subtotal: ["subtotal"],
  "subtotal-with-discounts": ["subtotal-with-discounts"],
  total: ["total"],
  "signer-name": ["signer-name"],
  "signed-date": ["signer-name", "signed-date"],
  "cover-letter": ["cover-letter"],
  "payment-terms": ["payment-terms"],
  "delivery-terms": ["delivery-terms"],
  "terms-of-service": ["terms-of-service"],
};

/**
 * The primitive sample keys one bound quote-scope source needs filled in, so
 * the form can render inputs for a source without knowing how it is derived.
 * An unknown source needs none: the renderer resolves it to null.
 * @param source - The raw quote-scope source key from the document
 * @returns The primitive keys feeding that source
 */
export function sampleKeysForSource(source: string): readonly SampleFieldKey[] {
  return isFpFieldSource(source) ? SAMPLE_KEYS_BY_SOURCE[source] : [];
}

/**
 * Whether a raw source key is one of the quote-scope field sources.
 * @param source - The raw source key to test
 * @returns True when the key is a known quote-scope field source
 */
function isFpFieldSource(source: string): source is FpFieldSource {
  return FP_FIELD_SOURCES.some((candidate) => candidate === source);
}

/** The sample keys whose value is a `YYYY-MM-DD` calendar day. */
export const SAMPLE_DATE_KEYS: readonly SampleFieldKey[] = [
  "effective-date",
  "expiration-date",
  "signed-date",
];

/** The sample keys whose value is multiline markdown. */
export const SAMPLE_MARKDOWN_KEYS: readonly SampleFieldKey[] = [
  "cover-letter",
  "payment-terms",
  "delivery-terms",
  "terms-of-service",
];

/**
 * Builds the customer-safe projection the canvas binds in sample mode. The
 * result is a genuine `PublicQuote`, so the renderer, the show-when conditions,
 * the placeholder chips, and the totals all behave exactly as they do for a real
 * quote - there is no second data path to keep in step.
 * @param data - The hand-edited sample data for the template
 * @param configured - The organization's configured values, keyed by sample key
 * @returns The projection to bind the canvas to
 */
export function buildSampleQuote(
  data: TemplateSampleData,
  configured: ConfiguredSampleValues = {},
): PublicQuote {
  const seeded: PublicQuote = { ...PLACEHOLDER_QUOTE, lines: [...data.lines] };
  const written = SAMPLE_FIELD_KEYS.reduce<PublicQuote>(
    (quote, key) =>
      SAMPLE_FIELD_WRITERS[key](
        quote,
        resolveSampleValue(key, data.fields, configured),
      ),
    seeded,
  );
  return { ...written, customFields: readCustomFields(data.fields) };
}

/**
 * The raw, editable value a sample line holds for a line-scope source, so an
 * input round-trips what the author typed rather than the rendered form.
 * @param line - The sample line row
 * @param source - The `fp-line-field` source key
 * @returns The stored value, or "" when the line carries none
 */
export function readSampleLineValue(
  line: PublicQuoteLine,
  source: string,
): string {
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    const name = source.slice(FP_CUSTOM_SOURCE_PREFIX.length);
    const value = line.type === "ITEM" ? line.customFields?.[name] : null;
    return value === null || value === undefined ? "" : String(value);
  }
  if (source === "description") {
    return line.description;
  }
  if (line.type !== "ITEM") {
    return "";
  }
  if (source === "unit") {
    return line.unit ?? "";
  }
  return readItemLineValue(line.item, line.quantityBreaks[0], source);
}

/**
 * Writes an author-typed value into the sample line property the given
 * line-scope source reads back, so the canvas shows what was typed.
 * Read-only sources (sequential numbering, item images) return the line
 * unchanged.
 * @param line - The sample line row
 * @param source - The `fp-line-field` source key
 * @param value - The author-typed value
 * @returns The updated line
 */
export function writeSampleLineValue(
  line: PublicQuoteLine,
  source: string,
  value: string,
): PublicQuoteLine {
  if (source === "description") {
    return { ...line, description: value };
  }
  if (line.type !== "ITEM") {
    return line;
  }
  if (source.startsWith(FP_CUSTOM_SOURCE_PREFIX)) {
    const name = source.slice(FP_CUSTOM_SOURCE_PREFIX.length);
    return { ...line, customFields: { ...line.customFields, [name]: value } };
  }
  return writeItemLineValue(line, source, value);
}

/**
 * Appends a fresh sample item row, cloning the last one so its columns stay
 * populated and the new row differs only by id.
 * @param lines - The current sample lines
 * @returns The lines with one more item row
 */
export function addSampleItemLine(
  lines: readonly PublicQuoteLine[],
): PublicQuoteLine[] {
  const lastItem = [...lines].reverse().find(isItemLine);
  const template = lastItem ?? FALLBACK_SAMPLE_ITEM;
  return [...lines, { ...template, id: crypto.randomUUID() }];
}

/** The row a template with no item rows left clones from. */
const FALLBACK_SAMPLE_ITEM: SampleItemLine = SAMPLE_SEED_LINES.find(
  isItemLine,
) ?? {
  id: "sample-item",
  type: "ITEM",
  description: "Sample line item",
  amount: "0.00",
  quantityBreaks: [],
  item: null,
  unit: null,
};

/** A sample line row carrying an item, its quantity break, and its price. */
type SampleItemLine = Extract<PublicQuoteLine, { type: "ITEM" }>;

/**
 * Whether a sample line is an item row (the only row type authors add, remove,
 * and price).
 * @param line - The sample line row
 * @returns True when the line is an item row
 */
export function isItemLine(line: PublicQuoteLine): line is SampleItemLine {
  return line.type === "ITEM";
}

/** The `PublicQuote` members a per-key sample writer produces. */
type SampleFieldWriter = (quote: PublicQuote, value: string) => PublicQuote;

/** An organization stand-in, so org keys have something to write into. */
const EMPTY_ORGANIZATION: NonNullable<PublicQuote["organization"]> = {
  id: "sample-organization",
  name: "",
  logoUrl: null,
  address: null,
  phone: null,
  fax: null,
  website: null,
};

/** A location stand-in, so location keys have something to write into. */
const EMPTY_LOCATION: NonNullable<PublicQuote["location"]> = {
  name: "",
  address: null,
  logoUrl: null,
};

/** A customer stand-in, so customer keys have something to write into. */
const EMPTY_COMPANY: NonNullable<PublicQuote["customerCompany"]> = {
  name: "",
  address: null,
  phone: null,
  email: null,
};

/**
 * Per-key writers placing an author-typed value where the matching resolver in
 * `quoteFields.ts` reads it back. Total over the primitive keys, so a new key
 * cannot be added to the form without a write path.
 */
const SAMPLE_FIELD_WRITERS: Record<SampleFieldKey, SampleFieldWriter> = {
  "org-name": (quote, value) => withOrganization(quote, { name: value }),
  "org-address": (quote, value) =>
    withOrganization(quote, { address: singleLineAddress(value) }),
  "org-website": (quote, value) =>
    withOrganization(quote, { website: emptyToNull(value) }),
  "org-phone": (quote, value) =>
    withOrganization(quote, { phone: emptyToNull(value) }),
  "org-fax": (quote, value) =>
    withOrganization(quote, { fax: emptyToNull(value) }),
  "location-name": (quote, value) => withLocation(quote, { name: value }),
  "location-address": (quote, value) =>
    withLocation(quote, { address: singleLineAddress(value) }),
  "customer-company-name": (quote, value) =>
    withCompany(quote, { name: value }),
  "customer-company-phone": (quote, value) =>
    withCompany(quote, { phone: emptyToNull(value) }),
  "customer-company-email": (quote, value) =>
    withCompany(quote, { email: emptyToNull(value) }),
  "customer-company-address": (quote, value) =>
    withCompany(quote, { address: singleLineAddress(value) }),
  "quote-people-names": (quote, value) => {
    const [firstName, ...rest] = value.split(" ");
    return withPerson(quote, { firstName, lastName: rest.join(" ") });
  },
  "quote-people-emails": (quote, value) =>
    withPerson(quote, { email: emptyToNull(value) }),
  "ship-to-address": (quote, value) => ({
    ...quote,
    shippingAddress: singleLineAddress(value),
  }),
  "opportunity-name": (quote, value) => ({
    ...quote,
    opportunity: value === "" ? null : { name: value },
  }),
  "sales-rep-name": (quote, value) => ({
    ...quote,
    salesRepName: emptyToNull(value),
  }),
  "sales-rep-email": (quote, value) => ({
    ...quote,
    salesRepEmail: emptyToNull(value),
  }),
  "estimator-names": (quote, value) => ({
    ...quote,
    estimatorNames: value === "" ? [] : [value],
  }),
  "quote-name": (quote, value) => ({ ...quote, name: emptyToNull(value) }),
  "quote-number": (quote, value) => ({ ...quote, quoteDisplayNumber: value }),
  "effective-date": (quote, value) => ({
    ...quote,
    createdAt: parseSampleDate(value),
  }),
  "expiration-date": (quote, value) => ({
    ...quote,
    expirationDate: emptyToNull(value),
  }),
  "quote-footer": (quote, value) => ({
    ...quote,
    quoteFooter: emptyToNull(value),
  }),
  subtotal: (quote, value) => ({ ...quote, subtotal: numericOrNull(value) }),
  "subtotal-with-discounts": (quote, value) => ({
    ...quote,
    subtotalWithDiscounts: numericOrNull(value),
  }),
  total: (quote, value) => ({ ...quote, total: numericOrNull(value) }),
  "signer-name": (quote, value) =>
    withSignature(quote, { ...signatureOf(quote), name: value }),
  "signed-date": (quote, value) =>
    withSignature(quote, {
      ...signatureOf(quote),
      signedAt: parseSampleDate(value),
    }),
  "cover-letter": (quote, value) => ({
    ...quote,
    coverLetterMarkdown: emptyToNull(value),
  }),
  "payment-terms": (quote, value) => ({
    ...quote,
    paymentTerms: emptyToNull(value),
  }),
  "delivery-terms": (quote, value) => ({
    ...quote,
    deliveryTerms: emptyToNull(value),
  }),
  "terms-of-service": (quote, value) => ({
    ...quote,
    termsOfService: emptyToNull(value),
  }),
};

/**
 * Merges a patch into the sample quote's organization, creating it on first
 * write since the placeholder quote carries none.
 * @param quote - The sample quote so far
 * @param patch - The organization members to set
 * @returns The updated quote
 */
function withOrganization(
  quote: PublicQuote,
  patch: Partial<NonNullable<PublicQuote["organization"]>>,
): PublicQuote {
  return {
    ...quote,
    organization: { ...(quote.organization ?? EMPTY_ORGANIZATION), ...patch },
  };
}

/**
 * Merges a patch into the sample quote's location.
 * @param quote - The sample quote so far
 * @param patch - The location members to set
 * @returns The updated quote
 */
function withLocation(
  quote: PublicQuote,
  patch: Partial<NonNullable<PublicQuote["location"]>>,
): PublicQuote {
  return {
    ...quote,
    location: { ...(quote.location ?? EMPTY_LOCATION), ...patch },
  };
}

/**
 * Merges a patch into the sample quote's customer company.
 * @param quote - The sample quote so far
 * @param patch - The company members to set
 * @returns The updated quote
 */
function withCompany(
  quote: PublicQuote,
  patch: Partial<NonNullable<PublicQuote["customerCompany"]>>,
): PublicQuote {
  return {
    ...quote,
    customerCompany: { ...(quote.customerCompany ?? EMPTY_COMPANY), ...patch },
  };
}

/** A person stand-in, so people keys have something to write into. */
const BLANK_PERSON: PublicQuote["people"][number] = {
  firstName: "",
  lastName: "",
  email: null,
};

/**
 * Merges a patch into the sample quote's single person.
 * @param quote - The sample quote so far
 * @param patch - The person members to set
 * @returns The updated quote
 */
function withPerson(
  quote: PublicQuote,
  patch: Partial<PublicQuote["people"][number]>,
): PublicQuote {
  return {
    ...quote,
    people: [{ ...(quote.people[0] ?? BLANK_PERSON), ...patch }],
  };
}

/** The signature written so far, or a blank one to merge the next key into. */
const BLANK_SIGNATURE: NonNullable<PublicQuote["signature"]> = {
  name: "",
  signedAt: new Date(NaN),
};

/**
 * The signature built so far, so the name and date keys compose into one.
 * @param quote - The sample quote so far
 * @returns The current signature, or a blank one
 */
function signatureOf(
  quote: PublicQuote,
): NonNullable<PublicQuote["signature"]> {
  return quote.signature ?? BLANK_SIGNATURE;
}

/**
 * Sets the sample quote's signature, keeping `isSigned` in step so the `signed`
 * show-when condition holds exactly when the author filled either half in.
 * @param quote - The sample quote so far
 * @param signature - The signature built from the name and date keys
 * @returns The updated quote
 */
function withSignature(
  quote: PublicQuote,
  signature: NonNullable<PublicQuote["signature"]>,
): PublicQuote {
  const isSigned =
    signature.name !== "" || !Number.isNaN(signature.signedAt.getTime());
  return {
    ...quote,
    signature: isSigned ? signature : null,
    isSigned,
  };
}

/**
 * A one-line address holding the author-typed text verbatim: every other member
 * is null, so `formatAddressLines` yields exactly what was typed.
 * @param value - The author-typed address line
 * @returns The address, or null when nothing was typed
 */
function singleLineAddress(value: string): Address | null {
  if (value === "") {
    return null;
  }
  return {
    name: null,
    address_line_1: value,
    address_line_2: null,
    city: null,
    zipcode: null,
    state: null,
    country: null,
  };
}

/**
 * Parses an author-typed `YYYY-MM-DD` value as a local calendar day. An empty
 * or unparseable value yields an invalid date, which the long-form formatter
 * renders as "" so the placeholder chip returns.
 * @param value - The date input's value
 * @returns The parsed date, or an invalid date
 */
function parseSampleDate(value: string): Date {
  if (value === "") {
    return new Date(NaN);
  }
  return new Date(`${value}T00:00:00`);
}

/**
 * The author-typed value when it is a number, else null, so a typo cannot reach
 * the currency formatters.
 * @param value - The author-typed amount
 * @returns The amount, or null when it is not numeric
 */
function numericOrNull(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  try {
    return new Decimal(trimmed).isFinite() ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * The author-typed value, or null when nothing was typed, matching how a real
 * quote stores an unfilled optional field.
 * @param value - The author-typed value
 * @returns The value, or null when empty
 */
function emptyToNull(value: string): string | null {
  return value === "" ? null : value;
}

/**
 * The quote-scope custom field values held in the sample data, keyed by the
 * name a `custom:<name>` source looks up.
 * @param fields - The sample field overrides
 * @returns The custom field values, or null when none were filled in
 */
function readCustomFields(
  fields: Record<string, string>,
): CustomFieldValues | null {
  const entries = Object.entries(fields)
    .filter(([key]) => key.startsWith(FP_CUSTOM_SOURCE_PREFIX))
    .map<[string, string]>(([key, value]) => [
      key.slice(FP_CUSTOM_SOURCE_PREFIX.length),
      value,
    ]);
  return entries.length === 0 ? null : Object.fromEntries(entries);
}

/**
 * The raw stored value of an item-row source.
 * @param item - The row's item, or null when it carries none
 * @param quantityBreak - The row's single quantity break, or undefined
 * @param source - The `fp-line-field` source key
 * @returns The stored value, or "" when absent or read-only
 */
function readItemLineValue(
  item: PublicQuoteItem | null,
  quantityBreak: PublicQuoteQuantityBreak | undefined,
  source: string,
): string {
  switch (source) {
    case "item-name":
      return item?.name ?? "";
    case "item-description":
      return item?.description ?? "";
    case "item-revision":
      return item?.revision ?? "";
    case "weight":
      return item?.weight ?? "";
    case "weight-unit":
      return item?.weightUnit ?? "";
    case "length":
      return item?.length ?? "";
    case "width":
      return item?.width ?? "";
    case "height":
      return item?.height ?? "";
    case "dimension-unit":
      return item?.dimensionUnit ?? "";
    case "quantity":
      return quantityBreak?.quantity ?? "";
    case "unit-price":
      return quantityBreak?.unitPrice ?? "";
    case "amount":
      return quantityBreak?.amount ?? "";
    default:
      return "";
  }
}

/**
 * Writes an item-row source into the item or its single quantity break, keeping
 * the break's total weight in step so `total-weight:` sums track the edit.
 * @param line - The sample item row
 * @param source - The `fp-line-field` source key
 * @param value - The author-typed value
 * @returns The updated line
 */
function writeItemLineValue(
  line: SampleItemLine,
  source: string,
  value: string,
): PublicQuoteLine {
  if (source === "amount") {
    return {
      ...line,
      amount: emptyToNull(value),
      quantityBreaks: patchBreak(line, { amount: emptyToNull(value) }),
    };
  }
  if (source === "unit-price") {
    return {
      ...line,
      quantityBreaks: patchBreak(line, { unitPrice: emptyToNull(value) }),
    };
  }
  if (source === "quantity") {
    return withTrackedWeight({
      ...line,
      quantityBreaks: patchBreak(line, { quantity: value }),
    });
  }
  if (source === "weight") {
    return withTrackedWeight({
      ...line,
      item: patchItem(line, { weight: emptyToNull(value) }),
    });
  }
  if (source === "weight-unit") {
    return {
      ...line,
      item: patchItem(line, { weightUnit: toWeightUnit(value) }),
    };
  }
  if (source === "length") {
    return { ...line, item: patchItem(line, { length: emptyToNull(value) }) };
  }
  if (source === "width") {
    return { ...line, item: patchItem(line, { width: emptyToNull(value) }) };
  }
  if (source === "height") {
    return { ...line, item: patchItem(line, { height: emptyToNull(value) }) };
  }
  if (source === "dimension-unit") {
    return {
      ...line,
      item: patchItem(line, { dimensionUnit: toDimensionUnit(value) }),
    };
  }
  if (source === "unit") {
    return { ...line, unit: toItemUnit(value) };
  }
  if (source === "item-name") {
    return { ...line, item: patchItem(line, { name: value }) };
  }
  if (source === "item-description") {
    return {
      ...line,
      item: patchItem(line, { description: emptyToNull(value) }),
    };
  }
  if (source === "item-revision") {
    return { ...line, item: patchItem(line, { revision: value }) };
  }
  return line;
}

/** The item a row falls back to when it carries none. */
const EMPTY_SAMPLE_ITEM: PublicQuoteItem = {
  name: "",
  revision: "",
  description: null,
  unit: "each",
  weight: null,
  weightUnit: null,
  length: null,
  width: null,
  height: null,
  dimensionUnit: null,
  primaryFileAsset: null,
};

/** The quantity break a row falls back to when it carries none. */
const EMPTY_SAMPLE_BREAK: PublicQuoteQuantityBreak = {
  quantity: "1",
  unitPrice: null,
  unitPriceOverride: null,
  amount: null,
  totalWeight: null,
  pricingUnit: null,
};

/**
 * The row's item with a patch applied.
 * @param line - The sample item row
 * @param patch - The item members to set
 * @returns The updated item
 */
function patchItem(
  line: SampleItemLine,
  patch: Partial<PublicQuoteItem>,
): PublicQuoteItem {
  return { ...(line.item ?? EMPTY_SAMPLE_ITEM), ...patch };
}

/**
 * The row's quantity breaks with a patch applied to its single break. Sample
 * rows keep exactly one break; multi-break sampling is out of scope.
 * @param line - The sample item row
 * @param patch - The break members to set
 * @returns The updated single-element break list
 */
function patchBreak(
  line: SampleItemLine,
  patch: Partial<PublicQuoteQuantityBreak>,
): PublicQuoteQuantityBreak[] {
  return [{ ...(line.quantityBreaks[0] ?? EMPTY_SAMPLE_BREAK), ...patch }];
}

/**
 * Recomputes the row's break total weight as unit weight times quantity, so the
 * read-only `total-weight:<unit>` fields track edits to either.
 * @param line - The sample item row
 * @returns The row with its break total weight in step
 */
function withTrackedWeight(line: SampleItemLine): PublicQuoteLine {
  const weight = line.item?.weight;
  const quantity = line.quantityBreaks[0]?.quantity;
  const totalWeight =
    weight && quantity && numericOrNull(weight) && numericOrNull(quantity)
      ? new Decimal(weight).times(quantity).toString()
      : null;
  return { ...line, quantityBreaks: patchBreak(line, { totalWeight }) };
}

/**
 * The weight unit an author picked, or null when the value is not one.
 * @param value - The picked value
 * @returns The weight unit, or null
 */
function toWeightUnit(value: string): PublicQuoteItem["weightUnit"] {
  const parsed = WeightUnitSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The dimension unit an author picked, or null for anything else.
 * @param value - The picked value
 * @returns The dimension unit, or null when unrecognised
 */
function toDimensionUnit(value: string): PublicQuoteItem["dimensionUnit"] {
  const parsed = DimensionUnitSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The item unit an author picked, falling back to "each" for anything else.
 * @param value - The picked value
 * @returns The item unit
 */
function toItemUnit(value: string): ItemUnit {
  const parsed = ItemUnitSchema.safeParse(value);
  return parsed.success ? parsed.data : "each";
}
